/**
 * 批量任务枚举数据的自定义 hooks
 */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { getAllFuncTypes, getAllTaskStatuses } from '@/service/batchOpt';
import { setBatchTaskFuncTypes, setBatchTaskStatuses } from '@/store/enum/actions';
import { iBatchTaskFuncTypeEnum, iBatchTaskStatusEnum } from '@/store/enum/type';

interface RootState {
  enumStore: {
    batchTaskFuncTypes: iBatchTaskFuncTypeEnum[] | null;
    batchTaskStatuses: iBatchTaskStatusEnum[] | null;
  };
}

/**
 * 获取批量任务功能类型枚举
 * 首次调用时从后端获取并存储到 store，后续从 store 读取
 * @returns {Array} 功能类型选项数组
 */
export const useBatchTaskFuncTypes = () => {
  const dispatch = useDispatch();
  const { batchTaskFuncTypes } = useSelector((state: RootState) => state.enumStore);
  const [loading, setLoading] = useState(batchTaskFuncTypes === null);

  useEffect(() => {
    // 如果 store 中已有数据（包括空数组），不再请求
    if (batchTaskFuncTypes !== null) {
      return;
    }

    // 从后端获取功能类型枚举
    const fetchFuncTypes = async () => {
      setLoading(true);
      try {
        const res = await getAllFuncTypes();
        if (!res.data.success) {
          message.error('获取功能类型枚举失败');
        }
        const rawData = Array.isArray(res.data?.data) ? res.data.data : [];
        // 转换后端数据格式为前端需要的格式
        const formattedData: iBatchTaskFuncTypeEnum[] = rawData.map((item: any) => ({
          label: item.first || '',
          value: item.second || ''
        }));
        dispatch(setBatchTaskFuncTypes(formattedData));
      } catch (error) {
        console.error('获取功能类型枚举失败:', error);
        message.error('获取功能类型枚举失败');
        dispatch(setBatchTaskFuncTypes([]));
      } finally {
        setLoading(false);
      }
    };

    fetchFuncTypes();
  }, [batchTaskFuncTypes, dispatch]);

  return { funcTypes: batchTaskFuncTypes ?? [], loading };
};

/**
 * 获取批量任务状态枚举
 * 首次调用时从后端获取并存储到 store，后续从 store 读取
 * @returns {Array} 任务状态选项数组
 */
export const useBatchTaskStatuses = () => {
  const dispatch = useDispatch();
  const { batchTaskStatuses } = useSelector((state: RootState) => state.enumStore);
  const [loading, setLoading] = useState(batchTaskStatuses === null);

  useEffect(() => {
    // 如果 store 中已有数据（包括空数组），不再请求
    if (batchTaskStatuses !== null) {
      return;
    }

    // 从后端获取任务状态枚举
    const fetchStatuses = async () => {
      setLoading(true);
      try {
        const res = await getAllTaskStatuses();
        if (!res.data.success) {
          message.error('获取任务状态枚举失败');
        }
        const rawData = Array.isArray(res.data?.data) ? res.data.data : [];
        // 转换后端数据格式为前端需要的格式
        const formattedData: iBatchTaskStatusEnum[] = rawData.map((item: any) => ({
          label: item.first || '',
          value: item.second ?? 0
        }));
        dispatch(setBatchTaskStatuses(formattedData));
      } catch (error) {
        console.error('获取任务状态枚举失败:', error);
        message.error('获取任务状态枚举失败');
        dispatch(setBatchTaskStatuses([]));
      } finally {
        setLoading(false);
      }
    };

    fetchStatuses();
  }, [batchTaskStatuses, dispatch]);

  return { statuses: batchTaskStatuses ?? [], loading };
};

/**
 * 同时获取功能类型和任务状态枚举
 * @returns {Object} 包含功能类型和任务状态的对象
 */
export const useBatchTaskEnums = () => {
  const { funcTypes, loading: funcTypesLoading } = useBatchTaskFuncTypes();
  const { statuses, loading: statusesLoading } = useBatchTaskStatuses();

  return {
    funcTypes,
    statuses,
    loading: funcTypesLoading || statusesLoading
  };
};
